import { useRef, useState, useEffect } from 'react';
import { useQuery, useMutation, skipToken } from '@apollo/client/react';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import { Navigate, useParams, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { mdiCog, mdiPlus } from '@mdi/js';
import type { Value } from 'platejs';
import { DateTime } from 'luxon';

import { Icon } from 'common/components/Icon';
import { Link } from 'common/components/Link';
import { BasePage } from 'hydra/pages/BasePage';
import {
    IOfferEventType,
    OfferDocument,
    OffersSettingsDocument,
    UpdateOfferDocument,
} from 'graphql/types';
import { Loader } from 'common/components/Loader';
import { ApolloError } from 'common/components/ApolloError';
import { ActionButton } from 'common/components/ActionButton';
import { Tooltip } from 'common/components/Tooltip';
import {
    PageHeaderBackButton,
    PageIntroHeader,
} from 'common/components/PageIntroHeader';
import { isUUID } from 'common/utils/isUUID';
import { getUrl } from 'route/utils/getUrl';
import { OfferEventList } from 'offer/components/OfferEventList';
import { ContentEditor } from 'common/components/ContentEditor';
import { useRouteMatch } from 'route/hooks/useRouteMatch';
import {
    ExitPageAlert,
    IExitPageAlertRef,
} from 'common/components/ExitPageAlert';
import { UnsavedChangesAlert } from 'common/components/Alerts';
import { deserializeContent } from 'common/components/ContentEditor/utils/deserialize';
import { Button } from 'common/components/Button';
import { UpdateOfferDrawer } from 'offer/components/UpdateOfferDrawer';
import { useOfferPermissions } from 'offer/hooks/useOfferPermissions';
import { CreateOfferEventDrawer } from 'offer/components/CreateOfferEventDrawer';
import { goToOfferDetail, goToOfferEditContent } from 'offer/utils/goToRoutes';
import { Chip, ChipGroup } from 'common/components/Chip';
import { PageTitle } from 'common/components/PageTitle';
import { OfferPermissionsContext } from 'offer/contexts';
import { useApolloError } from 'common/hooks/useApolloError';
import { useSnackbar } from 'common/hooks/useSnackbar';
import { DateRangeFilter } from 'common/components/filters';
import { TDateRange } from 'common/types';

export const OfferPage = () => {
    const [translate] = useTranslation();
    const navigate = useNavigate();
    const { id } = useParams();

    const [offerContent, setOfferContent] = useState<Value>();
    const [contentChanged, setContentChanged] = useState<boolean>(false);
    const [dateFilter, setDateFilter] = useState<TDateRange>({
        startDate: DateTime.now().startOf('day'),
        endDate: undefined,
    });
    const editing = !!useRouteMatch('OFFER_EDIT_CONTENT');
    const prevDescription = useRef<Value>(undefined);
    const exitAlertRef = useRef<IExitPageAlertRef>(null);
    const isEditPage = !!useRouteMatch('OFFER_EDIT');
    const isEventCreatePage = !!useRouteMatch('CREATE_OFFER_EVENT');
    const { showApolloError } = useApolloError();
    const [displaySnackbar] = useSnackbar();
    const [firstOfferEventType, setFirstOfferEventType] =
        useState<IOfferEventType>();

    const {
        data,
        loading: offerLoading,
        error,
    } = useQuery(
        OfferDocument,
        !id
            ? skipToken
            : {
                  variables: { id },
              }
    );

    useEffect(() => {
        const offer = data?.offer;

        if (!offer) return;

        // Set current offer description
        const description = deserializeContent(offer?.description);

        prevDescription.current = description;
        setOfferContent(description);
    }, [data]);

    const {
        data: offersSettingsData,
        loading: offersSettingsLoading,
        error: offersSettingsError,
    } = useQuery(OffersSettingsDocument);

    useEffect(() => {
        if (offersSettingsError) {
            showApolloError(offersSettingsError);
        }
    }, [offersSettingsError, showApolloError]);
    const { publishElearning, publishMeetingWebinar, publishTraining } =
        offersSettingsData?.offersSettings || {};

    const loading = offerLoading || offersSettingsLoading;

    const offer = data?.offer;
    const permissions = offer?.permissions
        ? JSON.parse(offer?.permissions)
        : undefined;
    const { canUpdate = false } = permissions || {};

    const { currentUserIsAuthor } = useOfferPermissions();

    const [
        updateOffer,
        { loading: updateOfferLoading, error: updateOfferError },
    ] = useMutation(UpdateOfferDocument);

    if (!id) return null;

    if (!isUUID(id) || (!loading && !offer)) {
        return <Navigate to={getUrl('PAGE_NOT_FOUND')} />;
    }

    // Early return when loading or error
    if (loading || error) {
        return (
            <BasePage>
                {error && <ApolloError error={error} />}
                {loading && <Loader />}
            </BasePage>
        );
    }

    // Redirect if on editing route and user does not have the correct role to edit
    if (!canUpdate && (editing || isEditPage)) {
        return (
            <Navigate
                to={getUrl('OFFER_DETAIL', {
                    id,
                })}
            />
        );
    }

    if (!loading && !offer?.hasDetailPage) {
        return <Navigate to={getUrl('OFFER_LIST')} />;
    }

    const { title, image, titleColor } = offer || {};

    let headerColor = titleColor;

    if (!headerColor) {
        headerColor = image ? 'common.white' : undefined;
    }

    const headerBackButton = (
        <PageHeaderBackButton color={headerColor} to="OFFER_LIST" />
    );

    let header;

    const authorChip = currentUserIsAuthor && canUpdate && (
        <Box sx={{ mb: 1 }}>
            <ChipGroup>
                <Chip bgColor="secondary" label={translate('author')} />
            </ChipGroup>
        </Box>
    );

    const updateButtons = canUpdate && (
        <Grid component={Box} sx={{ display: 'flex' }}>
            <Box sx={{ mr: 1 }}>
                <ActionButton
                    component={Link}
                    outlined={!image}
                    params={{ id }}
                    to={editing ? 'OFFER_DETAIL' : 'OFFER_EDIT_CONTENT'}
                    variant="extended"
                >
                    <>{translate(editing ? 'closeEditor' : 'edit')}</>
                </ActionButton>
            </Box>

            <Tooltip title={translate('management')}>
                <Box>
                    <ActionButton
                        aria-label={translate('management')}
                        component={Link}
                        outlined={!image}
                        params={{ id }}
                        to="OFFER_EDIT"
                    >
                        <Icon path={mdiCog} />
                    </ActionButton>
                </Box>
            </Tooltip>
        </Grid>
    );

    if (image) {
        header = (
            <PageIntroHeader backButton={headerBackButton} image={image}>
                {authorChip}

                <Grid
                    container
                    spacing={2}
                    sx={{
                        alignItems: 'flex-end',
                        justifyContent: 'space-between',
                    }}
                >
                    <Grid>
                        <Box
                            component="span"
                            sx={{
                                alignItems: 'center',
                                display: 'flex',
                                flexWrap: 'wrap',
                            }}
                        >
                            <Box sx={{ mr: 1 }}>
                                <Box sx={{ mb: 1 }}>
                                    <Typography
                                        sx={{ color: headerColor }}
                                        variant="h1"
                                    >
                                        {title}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Grid>

                    {updateButtons}
                </Grid>
            </PageIntroHeader>
        );
    }

    const handleSaveContent = async () => {
        const newDescription = JSON.stringify(offerContent);

        await updateOffer({
            variables: {
                id,
                offer: { description: newDescription },
            },
        });

        displaySnackbar(translate('offer.contentSaveSuccess'), {
            variant: 'success',
        });

        setContentChanged(false);

        // Set new version as prevVersion
        prevDescription.current = offerContent;
    };

    const handleCancelSaveContent = async () => {
        await setContentChanged(false);

        // Reset content
        await setOfferContent(
            prevDescription.current
                ? structuredClone(prevDescription.current)
                : undefined
        );

        setEditing(false);
    };

    const setEditing = (editing: boolean) => {
        if (!editing) {
            goToOfferDetail(navigate, id);

            return;
        }

        goToOfferEditContent(navigate, id);
    };

    const isMeetingWebinar = offer?.types.some(
        (type) =>
            type === IOfferEventType.Meeting || type === IOfferEventType.Webinar
    );

    const eventsTitle = (() => {
        const types = offer?.types;

        if (types?.includes(IOfferEventType.Training)) {
            return translate('offer.eventsTraining');
        }

        if (types?.includes(IOfferEventType.Elearning)) {
            return translate('offer.eventsElearning');
        }

        if (
            types?.includes(IOfferEventType.Meeting) ||
            types?.includes(IOfferEventType.Webinar)
        ) {
            return translate('offer.eventsMeetingWebinar');
        }

        return translate('offer.events');
    })();

    const fromDate = isMeetingWebinar
        ? ((dateFilter?.startDate ?? DateTime.now()).startOf('day').toISO() ??
          undefined)
        : undefined;

    const pageTitle = `${translate('offer.offersBrowserTitle')} - ${title}`;
    const canCreateNewEvent =
        canUpdate &&
        !loading &&
        (publishElearning || publishMeetingWebinar || publishTraining);

    return (
        <OfferPermissionsContext.Provider value={permissions}>
            <BasePage header={header}>
                {updateOfferError && <ApolloError error={updateOfferError} />}

                <PageTitle>{pageTitle}</PageTitle>

                <Box sx={{ py: 4 }}>
                    {!header && (
                        <Box sx={{ mb: 2 }}>
                            {headerBackButton}

                            {authorChip}

                            <Grid
                                container
                                spacing={2}
                                sx={{
                                    alignItems: 'flex-end',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <Grid>
                                    <Box sx={{ mb: 1 }}>
                                        <Typography
                                            sx={{
                                                color: headerColor,
                                            }}
                                            variant="h1"
                                        >
                                            {title}
                                        </Typography>
                                    </Box>
                                </Grid>

                                {updateButtons}
                            </Grid>
                        </Box>
                    )}

                    <Box sx={{ mb: 4 }}>
                        <ContentEditor
                            content={offerContent}
                            readOnly={!editing || !canUpdate}
                            onChange={(content) => {
                                setOfferContent(content);
                                setContentChanged(true);
                            }}
                        />

                        {editing && (
                            <Button
                                color="primary"
                                disabled={!contentChanged}
                                loading={updateOfferLoading}
                                variant="contained"
                                onClick={handleSaveContent}
                            >
                                {translate('save')}
                            </Button>
                        )}
                    </Box>

                    <Box>
                        <Box
                            sx={{
                                alignItems: 'center',
                                display: 'flex',
                                justifyContent: 'space-between',
                            }}
                        >
                            <Box
                                sx={{
                                    alignItems: 'center',
                                    display: 'flex',
                                    gap: 1,
                                }}
                            >
                                <Typography
                                    sx={{ fontWeight: 700, lineHeight: 1.2 }}
                                >
                                    {eventsTitle}
                                </Typography>

                                {canCreateNewEvent && (
                                    <Tooltip
                                        title={translate('offer.newEvent')}
                                    >
                                        <Box>
                                            <ActionButton
                                                outlined
                                                aria-label={translate(
                                                    'offer.newEvent'
                                                )}
                                                component={Link}
                                                params={{ id: offer?.id || '' }}
                                                size="medium"
                                                to="CREATE_OFFER_EVENT"
                                            >
                                                <Icon path={mdiPlus} />
                                            </ActionButton>
                                        </Box>
                                    </Tooltip>
                                )}
                            </Box>

                            {isMeetingWebinar && (
                                <DateRangeFilter
                                    disableEndDate
                                    dateRange={dateFilter}
                                    onChange={(dateRange) => {
                                        setDateFilter(
                                            dateRange ?? {
                                                startDate:
                                                    DateTime.now().startOf(
                                                        'day'
                                                    ),
                                                endDate: undefined,
                                            }
                                        );
                                    }}
                                />
                            )}
                        </Box>

                        <Divider sx={{ mt: 2 }} />

                        {offer && (
                            <OfferEventList
                                fromDate={fromDate}
                                offerId={offer.id}
                                setFirstOfferEventType={setFirstOfferEventType}
                            />
                        )}
                    </Box>

                    {offer && canUpdate && (
                        <UpdateOfferDrawer offer={offer} open={isEditPage} />
                    )}

                    {canCreateNewEvent && !!firstOfferEventType && (
                        <CreateOfferEventDrawer
                            offerEventType={firstOfferEventType}
                            open={isEventCreatePage}
                            onClose={() => goToOfferDetail(navigate, id)}
                        />
                    )}

                    <ExitPageAlert
                        alert={UnsavedChangesAlert}
                        ref={exitAlertRef}
                        when={editing && contentChanged}
                        onCancel={() => {
                            handleCancelSaveContent();

                            return true;
                        }}
                        onConfirm={async () => {
                            handleSaveContent();

                            return true;
                        }}
                    />
                </Box>
            </BasePage>
        </OfferPermissionsContext.Provider>
    );
};
